import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Music, Drum, Users, Star, Award, CheckCircle, Sparkles, Video,
  Calendar, MapPin, DollarSign, Printer, Download, ChevronDown, ChevronUp,
  Feather, Flame, Crown, Mic, FileDown
} from 'lucide-react';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';

/* ────────────────────────────────────────────────────────────────────────── */
/*  TIER DATA — kept as JS so admins can also edit inline via PageEditable   */
/* ────────────────────────────────────────────────────────────────────────── */

export const TIERS = [
  {
    id: 'headline',
    amount: 30000,
    amountLabel: '$30,000',
    duration: '12 months',
    tagline: 'The Full Production',
    subtitle: 'Ancestors Awaken — Feature Concert & Film',
    accent: 'from-yellow-500 via-amber-500 to-red-500',
    ring: 'ring-yellow-400/60',
    text: 'text-yellow-400',
    bullet: 'text-amber-400',
    highlight: true,
    narrative:
      "The complete vision. A ticketed evening-length concert film uniting a conductor-led chamber ensemble, seven master drummers, three dancers, and a single African masquerade — all captured with multi-camera coverage, professional audio mix, and cinematic edit. Rehearsed over twelve months, premiered in New Orleans, and released as a broadcast-quality film to schools, museums, and streaming partners worldwide.",
    ensemble: [
      '1 Conductor / Music Director',
      '5–6 piece chamber orchestra (strings + woodwinds)',
      '7 master drummers (Casa Samba core + guest masters)',
      '3 principal dancers (samba, orixá, contemporary)',
      '1 African masquerade artist (traditional regalia)'
    ],
    deliverables: [
      'One live premiere performance at a New Orleans venue',
      '60–75 minute cinematic concert film (multi-cam, mixed audio)',
      'Behind-the-scenes documentary short (10–15 min)',
      'Digital study guide for classrooms and community groups',
      'Free public matinee for schools and low-income families',
      'Perpetual streaming rights donated back to Casa Samba'
    ],
    budget: [
      { label: 'Videographer / cinematographer (multi-cam, 2 shoot days)', amount: 4000 },
      { label: 'Post-production video edit, color, delivery', amount: 2000 },
      { label: 'Live audio engineer + multi-track mix', amount: 1500 },
      { label: 'Conductor / music director honorarium', amount: 3500 },
      { label: 'Chamber orchestra (5–6 players × rehearsals + show)', amount: 3500 },
      { label: '7 drummers (rehearsals + performance stipends)', amount: 2800 },
      { label: '3 dancers (rehearsals + performance stipends)', amount: 1500 },
      { label: 'African masquerade artist (regalia + performance)', amount: 1500 },
      { label: 'Composer / arranger commission', amount: 2000 },
      { label: 'Guest-artist lodging (5 nights × 2 rooms)', amount: 1500 },
      { label: 'Venue rental (theatre / concert hall)', amount: 3500 },
      { label: 'Costume, instrument prep & staging', amount: 1000 },
      { label: 'Marketing, programs, social & graphics', amount: 1000 },
      { label: 'Administrative overhead (~2.3%)', amount: 700 }
    ]
  },
  {
    id: 'chamber',
    amount: 20000,
    amountLabel: '$20,000',
    duration: '9 months',
    tagline: 'The Chamber Concert',
    subtitle: 'Ancestral Voices — Filmed Concert',
    accent: 'from-orange-500 via-amber-500 to-yellow-500',
    ring: 'ring-orange-400/60',
    text: 'text-orange-300',
    bullet: 'text-orange-300',
    narrative:
      "A tighter, chamber-scale version of the concert. Nine months of rehearsal culminating in one filmed evening: reduced orchestra, five drummers, two dancers, and the African masquerade. Same cinematic quality of capture, distilled to its most essential voices.",
    ensemble: [
      '1 Conductor / Music Director',
      '3–4 piece chamber ensemble',
      '5 drummers',
      '2 principal dancers',
      '1 African masquerade artist'
    ],
    deliverables: [
      'One filmed live performance in New Orleans',
      '45–60 minute concert film (multi-cam, mixed audio)',
      'Free educational screening for one partner school',
      'Digital study guide (short version)',
      'Streaming rights donated back to Casa Samba'
    ],
    budget: [
      { label: 'Videographer / cinematographer (single shoot day)', amount: 2800 },
      { label: 'Post-production edit, color, delivery', amount: 1400 },
      { label: 'Live audio engineer + mix', amount: 1200 },
      { label: 'Conductor / music director honorarium', amount: 2500 },
      { label: 'Chamber ensemble (3–4 players)', amount: 2000 },
      { label: '5 drummers (rehearsals + performance)', amount: 2000 },
      { label: '2 dancers (rehearsals + performance)', amount: 1000 },
      { label: 'African masquerade artist', amount: 1200 },
      { label: 'Composer / arranger commission (short work)', amount: 1500 },
      { label: 'Guest-artist lodging (3 nights)', amount: 900 },
      { label: 'Venue rental', amount: 2500 },
      { label: 'Costume & instrument prep', amount: 500 },
      { label: 'Marketing & program', amount: 500 }
    ]
  },
  {
    id: 'circle',
    amount: 10000,
    amountLabel: '$10,000',
    duration: '6 months',
    tagline: 'The Community Circle',
    subtitle: 'Ancestral Circle — Community Showcase',
    accent: 'from-red-500 via-orange-500 to-amber-500',
    ring: 'ring-red-400/60',
    text: 'text-red-300',
    bullet: 'text-red-300',
    narrative:
      "A community-scale showcase — free and open to the neighbourhood. Six months of preparation, an intimate ensemble, and one filmed performance in a community hall. Designed to reach New Orleans first, on the ground, before it travels.",
    ensemble: [
      '1 Conductor / Music Director (Mestre Curtis Pierre)',
      '3-piece chamber ensemble',
      '4 drummers',
      '2 dancers',
      '1 African masquerade artist'
    ],
    deliverables: [
      'One free community performance in New Orleans',
      '30–45 minute concert film',
      'One outdoor / community-hall event with free admission',
      'Highlight reel for social media and outreach'
    ],
    budget: [
      { label: 'Videographer (single shoot)', amount: 1500 },
      { label: 'Post-production edit', amount: 700 },
      { label: 'Live sound engineer', amount: 700 },
      { label: 'Conductor / music director', amount: 1200 },
      { label: 'Chamber ensemble (3 players)', amount: 1200 },
      { label: '4 drummers', amount: 1200 },
      { label: '2 dancers', amount: 800 },
      { label: 'African masquerade artist', amount: 800 },
      { label: 'Guest-artist lodging (2 nights)', amount: 400 },
      { label: 'Venue / community hall rental', amount: 1200 },
      { label: 'Marketing & outreach', amount: 300 }
    ]
  },
  {
    id: 'pilot',
    amount: 5000,
    amountLabel: '$5,000',
    duration: '3 months',
    tagline: 'The Pilot Performance',
    subtitle: 'Roots — Pilot Showcase',
    accent: 'from-amber-600 via-orange-600 to-red-600',
    ring: 'ring-amber-500/60',
    text: 'text-amber-300',
    bullet: 'text-amber-300',
    narrative:
      "Three months, one intimate room, and a proof-of-concept performance. This tier lets us pilot the artistic vision — the fusion of ancestral string instruments, Brazilian drumming and African masquerade — on a small stage, on film, so the funder can see it and expand it later.",
    ensemble: [
      'Mestre Curtis Pierre (music director)',
      '3 drummers',
      '1 dancer',
      '1 African masquerade artist'
    ],
    deliverables: [
      'One filmed pilot performance in New Orleans',
      '15–20 minute concert short',
      'Written report + budget breakdown for funder',
      'Video assets suitable for expanded-tier fundraising'
    ],
    budget: [
      { label: 'Videographer (single shoot)', amount: 800 },
      { label: 'Post-production edit', amount: 600 },
      { label: 'Live sound engineer', amount: 400 },
      { label: 'Mestre Curtis + 3 drummers', amount: 900 },
      { label: '1 dancer', amount: 300 },
      { label: 'African masquerade artist', amount: 500 },
      { label: 'Venue / rehearsal space', amount: 600 },
      { label: 'Instrument & costume prep', amount: 300 },
      { label: 'Marketing (social + flyer)', amount: 200 },
      { label: 'Miscellaneous / contingency', amount: 400 }
    ]
  }
];

/* ────────────────────────────────────────────────────────────────────────── */
/*  TIER CARD                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

const TierCard = ({ tier, expanded, onToggle }) => {
  const p = `tier.${tier.id}`;
  const total = tier.budget.reduce((s, l) => s + l.amount, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative rounded-3xl border ${tier.highlight ? 'border-yellow-400' : 'border-white/10'} bg-[#0f0f0f] overflow-hidden ${tier.highlight ? 'ring-2 ring-yellow-400/40' : ''} print:border-gray-300 print:bg-white`}
    >
      {tier.highlight && (
        <div className="absolute top-0 right-0 bg-yellow-400 text-black text-[10px] font-black tracking-widest px-3 py-1 rounded-bl-xl uppercase">
          Recommended
        </div>
      )}

      <div className={`h-2 bg-gradient-to-r ${tier.accent}`} />

      <div className="p-6 md:p-7">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className={`${tier.text} text-xs font-bold tracking-[0.3em] uppercase mb-1`}>
              <PageEditable path={`${p}.tagline`} as="span" fallback={tier.tagline} />
            </p>
            <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">
              <PageEditable path={`${p}.subtitle`} as="span" fallback={tier.subtitle} />
            </h3>
          </div>
          <div className="text-right shrink-0">
            <p className="text-3xl md:text-4xl font-black text-white leading-none">
              <PageEditable path={`${p}.amountLabel`} as="span" fallback={tier.amountLabel} />
            </p>
            <p className="text-xs text-white/60 mt-1">
              <PageEditable path={`${p}.duration`} as="span" fallback={tier.duration} />
            </p>
          </div>
        </div>

        <PageEditable
          path={`${p}.narrative`}
          as="p"
          multiline
          className="text-white/80 leading-relaxed text-sm md:text-base mb-5"
          fallback={tier.narrative}
        />

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          {/* Ensemble */}
          <div className="bg-black/40 rounded-xl p-4 border border-white/5">
            <p className={`${tier.bullet} text-xs font-bold tracking-widest uppercase mb-2 flex items-center gap-1`}>
              <Users className="w-3 h-3" /> Ensemble
            </p>
            <ul className="space-y-1.5 text-sm text-white/80">
              {tier.ensemble.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className={`w-3.5 h-3.5 ${tier.bullet} mt-0.5 flex-shrink-0`} />
                  <PageEditable path={`${p}.ensemble.${i}`} as="span" multiline fallback={line} />
                </li>
              ))}
            </ul>
          </div>

          {/* Deliverables */}
          <div className="bg-black/40 rounded-xl p-4 border border-white/5">
            <p className={`${tier.bullet} text-xs font-bold tracking-widest uppercase mb-2 flex items-center gap-1`}>
              <Star className="w-3 h-3" /> Deliverables
            </p>
            <ul className="space-y-1.5 text-sm text-white/80">
              {tier.deliverables.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className={`w-3.5 h-3.5 ${tier.bullet} mt-0.5 flex-shrink-0`} />
                  <PageEditable path={`${p}.deliverables.${i}`} as="span" multiline fallback={line} />
                </li>
              ))}
            </ul>
          </div>
        </div>

        <button
          onClick={() => onToggle(tier.id)}
          data-edit-safe
          className={`w-full mt-2 py-2.5 rounded-xl border ${tier.highlight ? 'border-yellow-400/60 text-yellow-300 hover:bg-yellow-400/10' : 'border-white/20 text-white/80 hover:bg-white/5'} text-sm font-semibold flex items-center justify-center gap-2 transition-colors print:hidden`}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Hide budget breakdown' : 'View line-item budget'}
        </button>

        {/* PROMINENT PDF DOWNLOAD */}
        <Link
          to={`/ancestral-project/budget/${tier.id}`}
          target="_blank"
          rel="noopener noreferrer"
          data-edit-safe
          data-testid={`download-budget-${tier.id}`}
          className="mt-3 w-full py-3.5 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black font-black text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-yellow-500/20 print:hidden"
        >
          <FileDown className="w-5 h-5" />
          Download {tier.amountLabel} Budget PDF
        </Link>

        {(expanded || typeof window === 'undefined') && (
          <div className="mt-4 bg-black/60 rounded-xl border border-white/10 overflow-hidden print:block print:mt-6 print:bg-white print:border-gray-300">
            <div className="px-4 py-2 bg-white/5 border-b border-white/10 flex items-center justify-between print:bg-gray-100 print:border-gray-300">
              <p className="text-xs font-bold tracking-widest uppercase text-white/70 print:text-black">
                Line-item budget
              </p>
              <p className="text-xs text-white/50 print:text-gray-600">USD</p>
            </div>
            <ul className="divide-y divide-white/5 print:divide-gray-200">
              {tier.budget.map((line, i) => (
                <li key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-white/80 print:text-black">
                    <PageEditable path={`${p}.budget.${i}.label`} as="span" fallback={line.label} />
                  </span>
                  <span className="text-white/90 font-semibold tabular-nums print:text-black">
                    ${line.amount.toLocaleString()}
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between px-4 py-3 text-sm bg-white/5 print:bg-gray-100">
                <span className="text-white font-bold uppercase tracking-widest text-xs print:text-black">Total</span>
                <span className="text-yellow-400 font-black text-lg tabular-nums print:text-black">
                  ${total.toLocaleString()}
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  MAIN PAGE                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

function AncestralProjectInner() {
  const [expandedTier, setExpandedTier] = useState('headline');

  const toggleTier = (id) => setExpandedTier((cur) => (cur === id ? null : id));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white print:bg-white print:text-black">
      {/* ───── HERO ───── */}
      <section className="relative py-20 md:py-28 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-950/30 via-black to-amber-950/30" aria-hidden />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500" />

        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/40 text-yellow-300 text-xs font-bold tracking-widest uppercase mb-6"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <PageEditable path="hero.eyebrow" as="span" fallback="NOTCF Grant Proposal · Casa Samba" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-black tracking-tight mb-4"
          >
            <PageEditable
              path="hero.title"
              as="span"
              className="bg-gradient-to-br from-yellow-300 via-amber-400 to-red-500 bg-clip-text text-transparent"
              fallback="Ancestors Awaken"
            />
          </motion.h1>

          <PageEditable
            path="hero.subtitle"
            as="p"
            className="text-lg md:text-2xl text-white/80 max-w-3xl mx-auto mb-3"
            fallback="A one-of-a-kind Afro-Diasporic musical convergence — filmed live in New Orleans."
          />

          <PageEditable
            path="hero.description"
            as="p"
            multiline
            className="text-base md:text-lg text-white/60 max-w-3xl mx-auto mb-8 leading-relaxed"
            fallback="A conductor-led ensemble, seven master drummers, three dancers, and a single African masquerade — coming together for one evening under the direction of Mestre Curtis Pierre. Captured on film. Given to the community. Preserved for the ancestors."
          />

          <div className="flex flex-wrap justify-center gap-3 print:hidden">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-black rounded-full font-bold text-sm hover:bg-yellow-300 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-3 bg-yellow-500 text-black rounded-full font-bold text-sm hover:bg-yellow-400 transition-colors"
            >
              <Mic className="w-4 h-4" /> Contact Casa Samba
            </Link>
          </div>
        </div>
      </section>

      {/* ───── VISION ───── */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-yellow-400" />
            <h2 className="text-3xl md:text-4xl font-black">
              <PageEditable path="vision.title" as="span" fallback="The Vision" />
            </h2>
          </div>
          <PageEditable
            path="vision.body"
            as="p"
            multiline
            className="text-white/80 leading-relaxed text-lg mb-4"
            fallback="For one evening, the ancestors return through sound. A conductor lifts the baton over a small orchestra tuned to strings that predate any concert hall. Seven drummers answer in the rhythms of Bahia, of West Africa, of second-line New Orleans. Three dancers become the bridge between the seated and the standing. A single masquerade — mask carved by hand, feathers earned — moves through the room as a living reminder of who we are."
          />
          <PageEditable
            path="vision.body2"
            as="p"
            multiline
            className="text-white/80 leading-relaxed text-lg mb-4"
            fallback="This is not a lecture, and it is not a festival stage. It is a filmed live event — one night, one room, one continuous conversation between continents. Every dollar in this proposal exists to make that room, and that film, real."
          />
          <PageEditable
            path="vision.body3"
            as="p"
            multiline
            className="text-white/80 leading-relaxed text-lg"
            fallback="Casa Samba already owns the media-capture equipment. This grant funds the artists, the room, the film labor, and the audience — the human parts that only the funder can help us afford."
          />
        </div>
      </section>

      {/* ───── ENSEMBLE ───── */}
      <section className="py-16 md:py-20 px-6 bg-[#0d0d0d] border-y border-white/5 print:bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-yellow-400 text-xs font-bold tracking-[0.35em] uppercase mb-2">
              <PageEditable path="ensemble.eyebrow" as="span" fallback="The Full Ensemble" />
            </p>
            <h2 className="text-3xl md:text-4xl font-black">
              <PageEditable path="ensemble.title" as="span" fallback="Fifteen artists · one continuous evening" />
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { icon: Mic, label: 'Conductor', sub: 'Music Director' },
              { icon: Music, label: 'Small Orchestra', sub: '3–6 players' },
              { icon: Drum, label: '7 Drummers', sub: 'Casa Samba + guests' },
              { icon: Feather, label: '3 Dancers', sub: 'Samba · Orixá · Contemporary' },
              { icon: Crown, label: '1 Masquerade', sub: 'African regalia' }
            ].map((role, i) => (
              <div
                key={i}
                className="rounded-2xl bg-black/40 border border-white/10 p-5 text-center print:bg-white print:border-gray-300"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-yellow-500/20 to-red-500/20 border border-yellow-400/30 flex items-center justify-center">
                  <role.icon className="w-6 h-6 text-yellow-400" />
                </div>
                <p className="font-bold text-white text-sm md:text-base leading-tight">
                  <PageEditable path={`ensemble.role.${i}.label`} as="span" fallback={role.label} />
                </p>
                <p className="text-xs text-white/60 mt-1 print:text-gray-600">
                  <PageEditable path={`ensemble.role.${i}.sub`} as="span" fallback={role.sub} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── TIERS ───── */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-yellow-400 text-xs font-bold tracking-[0.35em] uppercase mb-2">
              <PageEditable path="tiersHeader.eyebrow" as="span" fallback="Four ways to fund this" />
            </p>
            <h2 className="text-3xl md:text-5xl font-black mb-4">
              <PageEditable path="tiersHeader.title" as="span" fallback="Choose the scope. We build to it." />
            </h2>
            <PageEditable
              path="tiersHeader.body"
              as="p"
              multiline
              className="text-white/70 max-w-3xl mx-auto text-base md:text-lg mb-6"
              fallback="Each tier is a complete, standalone production — funder chooses a single scope. Tiers can also stack: a $5,000 pilot can grow into a $30,000 full production if the funder returns for a second cycle. Timeline scales with tier: 3 months at $5k, 6 months at $10k, 9 months at $20k, 12 months at $30k."
            />

            {/* Quick download-all-PDFs bar */}
            <div className="inline-flex flex-wrap justify-center gap-2 rounded-2xl bg-yellow-500/10 border-2 border-yellow-400/40 px-5 py-4 print:hidden">
              <span className="text-yellow-300 text-xs font-black tracking-widest uppercase self-center mr-2">
                <FileDown className="w-4 h-4 inline mr-1" /> Budget PDFs:
              </span>
              {TIERS.map((t) => (
                <Link
                  key={t.id}
                  to={`/ancestral-project/budget/${t.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`download-budget-top-${t.id}`}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-full text-xs font-black transition-colors"
                >
                  {t.amountLabel}
                </Link>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {TIERS.map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                expanded={expandedTier === tier.id}
                onToggle={toggleTier}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ───── WHY CASA SAMBA ───── */}
      <section className="py-16 md:py-20 px-6 bg-[#0d0d0d] border-y border-white/5 print:bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-6 flex items-center gap-2">
            <Award className="w-6 h-6 text-yellow-400" />
            <PageEditable path="why.title" as="span" fallback="Why Casa Samba" />
          </h2>
          <PageEditable
            path="why.body"
            as="p"
            multiline
            className="text-white/80 leading-relaxed text-lg mb-4"
            fallback="Casa Samba was founded in 1987 by Mestre Curtis Pierre. On July 21, 1993, Mayor Sidney Bartholomew proclaimed Casa Samba the first samba school in New Orleans. For 38 years the organization has been the city's authority on Afro-Brazilian rhythm, dance and culture — with hundreds of performances at Jazz Fest, Zulu balls, Endymion, and in schools across the metropolitan area."
          />
          <PageEditable
            path="why.body2"
            as="p"
            multiline
            className="text-white/80 leading-relaxed text-lg mb-4"
            fallback="No other New Orleans organization holds this depth of Afro-Diasporic musical knowledge under one roof — with the media equipment, the venue relationships, the community trust, and the artistic direction needed to stage a filmed one-of-a-kind ensemble event. This grant does not build capacity. It activates capacity that already exists."
          />
          <PageEditable
            path="why.body3"
            as="p"
            multiline
            className="text-white/80 leading-relaxed text-lg"
            fallback="Casa Samba is the venue's own equipment holder, the film crew's music director, the drummers' Mestre, the dancers' choreographer, and the masquerade artist's neighbour. The efficiency ratio in this budget — the amount of art produced per dollar granted — is not accidental. It is what 38 years of building looks like."
          />
        </div>
      </section>

      {/* ───── GRANT Q&A ───── */}
      <section className="py-16 md:py-20 px-6 bg-[#0d0d0d] border-y border-white/5 print:bg-white print:border-gray-300 print:border-t-2">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-yellow-400 text-xs font-bold tracking-[0.35em] uppercase mb-2">
              <PageEditable path="qa.eyebrow" as="span" fallback="NOTCF Application · Written Responses" />
            </p>
            <h2 className="text-3xl md:text-5xl font-black mb-3">
              <PageEditable path="qa.title" as="span" fallback="Grant Application Q&A" />
            </h2>
            <p className="text-white/60 text-sm md:text-base print:text-gray-600">
              <PageEditable path="qa.subtitle" as="span" fallback="Each answer is kept under the 1,000-character NOTCF limit." />
            </p>
          </div>

          {/* Q1 */}
          <div className="mb-10 rounded-2xl bg-black/40 border border-white/10 p-6 md:p-8 print:bg-white print:border-gray-300">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-yellow-400 font-black text-2xl md:text-3xl">01</span>
              <p className="text-white/50 text-xs font-bold tracking-widest uppercase print:text-gray-600">
                <PageEditable path="qa.q1.tag" as="span" fallback="Novelty · You" />
              </p>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 leading-snug print:text-black">
              <PageEditable
                path="qa.q1.question"
                as="span"
                multiline
                fallback="Why are you the best person to work on this? What is novel in your approach?"
              />
            </h3>
            <PageEditable
              path="qa.q1.answer"
              as="div"
              multiline
              className="text-white/80 leading-relaxed whitespace-pre-line print:text-black"
              fallback={
                "As Mestre Curtis Pierre, I have spent 38 years — since founding Casa Samba in 1987 — becoming the Afro-Brazilian musical authority in the American South. In 1993 the Mayor of New Orleans proclaimed Casa Samba the city's first samba school. I am both the artistic director and the physical practitioner: I conduct the ensemble, teach the drummers, coach the dancers, and personally know the masquerade artist whose regalia will appear onstage.\n\nWhat is novel is the convergence itself. No New Orleans production has ever placed a conductor-led chamber orchestra, seven Afro-Brazilian drummers, three trained dancers, and a single African masquerade artist inside one filmed evening. Museums document these traditions separately. Casa Samba stages them speaking to each other — live, in one room, on one continuous timeline, captured with our own broadcast-grade equipment. The grant funds artists and space, not overhead. That is efficiency 38 years of practice produces."
              }
            />
          </div>

          {/* Q2 */}
          <div className="mb-10 rounded-2xl bg-black/40 border border-white/10 p-6 md:p-8 print:bg-white print:border-gray-300">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-yellow-400 font-black text-2xl md:text-3xl">02</span>
              <p className="text-white/50 text-xs font-bold tracking-widest uppercase print:text-gray-600">
                <PageEditable path="qa.q2.tag" as="span" fallback="Success · Vision" />
              </p>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 leading-snug print:text-black">
              <PageEditable
                path="qa.q2.question"
                as="span"
                multiline
                fallback="What could success look like 1 year from now? What will it look like if you're successful beyond your wildest dreams?"
              />
            </h3>
            <PageEditable
              path="qa.q2.answer"
              as="div"
              multiline
              className="text-white/80 leading-relaxed whitespace-pre-line print:text-black"
              fallback={
                "ONE YEAR:\nAncestors Awaken premieres in New Orleans as a filmed 60–75 minute concert — one conductor, five to six chamber players, seven master drummers, three dancers, one African masquerade. A free public matinee serves schools and low-income families. The film releases with a digital study guide and streaming rights donated back to Casa Samba. Documentation of the making-of runs 10–15 minutes and travels as an outreach tool.\n\nBEYOND WILDEST DREAMS:\nThe premiere becomes an annual New Orleans event. The film enters ethnomusicology curricula, PBS broadcasts, and international festivals. The ensemble tours — Bahia, Lagos, Kingston. Young New Orleanians grow up seeing their African, Brazilian, and Louisiana lineages onstage together, understood as one continuous inheritance. The Casa Samba archive becomes the world's authority on this convergence — not because we said so, but because for one evening, we simply made it audible."
              }
            />
          </div>

          {/* Q3 */}
          <div className="rounded-2xl bg-black/40 border border-white/10 p-6 md:p-8 print:bg-white print:border-gray-300">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-yellow-400 font-black text-2xl md:text-3xl">03</span>
              <p className="text-white/50 text-xs font-bold tracking-widest uppercase print:text-gray-600">
                <PageEditable path="qa.q3.tag" as="span" fallback="Failure · Honesty" />
              </p>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 leading-snug print:text-black">
              <PageEditable
                path="qa.q3.question"
                as="span"
                multiline
                fallback="What might failure look like? What could be some of the original mistakes of your project?"
              />
            </h3>
            <PageEditable
              path="qa.q3.answer"
              as="div"
              multiline
              className="text-white/80 leading-relaxed whitespace-pre-line print:text-black"
              fallback={
                "The clearest failure mode is spectacle without substance — a beautifully filmed evening that impresses grant reviewers but leaves the artists underpaid, the community uninvited, and the masquerade decontextualized. If Ancestors Awaken becomes a show ABOUT African and Brazilian traditions instead of a show BY the practitioners of those traditions, we have failed regardless of production quality.\n\nA second mistake would be treating this as a one-off event rather than a first cycle. The four-tier structure of this proposal exists precisely because scale should follow trust: a $5,000 pilot proves the concept; a $30,000 full production only makes sense if the community, the artists, and the funder are all still in the room.\n\nA third would be over-producing the film at the cost of the live experience. The audience in New Orleans on the night of the performance must feel that the room, and the ancestors, came first — and the camera came second."
              }
            />
          </div>

          <p className="mt-10 text-center text-xs text-white/40 tracking-widest uppercase print:text-gray-500">
            <PageEditable
              path="qa.footnote"
              as="span"
              fallback="Character counts verified · Casa Samba · Escola de Samba"
            />
          </p>
        </div>
      </section>

      {/* ───── CTA ───── */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            <PageEditable path="cta.title" as="span" fallback="Fund the room. Let the ancestors do the rest." />
          </h2>
          <PageEditable
            path="cta.body"
            as="p"
            multiline
            className="text-white/70 text-lg mb-8"
            fallback="For questions, budget clarifications, or to request additional documentation, please contact Mestre Curtis Pierre and the Casa Samba team."
          />

          <div className="flex flex-wrap justify-center gap-3 print:hidden">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-yellow-500 text-black rounded-full font-bold hover:bg-yellow-400 transition-colors"
            >
              <Mic className="w-4 h-4" /> Contact Casa Samba
            </Link>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/10 border border-white/20 text-white rounded-full font-bold hover:bg-white/15 transition-colors"
            >
              <Download className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>

          <p className="text-xs text-white/40 tracking-widest uppercase mt-10">
            <PageEditable path="cta.footer" as="span" fallback="Escola de Samba Casa Samba · New Orleans · Est. 1987" />
          </p>
        </div>
      </section>
    </div>
  );
}

const AncestralProject = () => (
  <PageEditProvider slug="ancestral-project">
    <AncestralProjectInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export { AncestralProject };
export default AncestralProject;
